import jsPDF from 'jspdf';
import { Invoice, Receipt, Contract, Estimate } from '@/services/billing/billingService';

export const generateEstimatePDF = (estimate: Estimate) => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('IT Management Solutions', 20, 30);
  
  doc.setFontSize(16);
  doc.text('ESTIMATE', 20, 50);
  
  // Estimate details
  doc.setFontSize(12);
  doc.text(`Estimate #: ${estimate.id.slice(-8)}`, 20, 70);
  doc.text(`Date: ${new Date(estimate.created_at).toLocaleDateString()}`, 20, 80);
  doc.text(`Status: ${estimate.status.toUpperCase()}`, 20, 90);
  
  if (estimate.wave_estimate_id) {
    doc.text(`Wave ID: ${estimate.wave_estimate_id}`, 20, 100);
  }
  
  // Line items
  doc.setFontSize(14);
  doc.text('SERVICES:', 20, 120);
  
  let yPosition = 135;
  estimate.line_items.forEach((item, index) => {
    doc.setFontSize(10);
    doc.text(`${index + 1}. ${item.description}`, 25, yPosition);
    doc.text(`Qty: ${item.quantity} × $${item.rate.toFixed(2)} = $${item.amount.toFixed(2)}`, 25, yPosition + 10);
    yPosition += 25;
  });
  
  // Total
  doc.setFontSize(16);
  doc.text(`Total: $${estimate.total.toFixed(2)}`, 20, yPosition + 20);
  
  // Footer
  doc.setFontSize(10);
  doc.text('Thank you for considering our services!', 20, 250);
  doc.text('IT Management Solutions - Professional IT Services', 20, 260);
  
  // Download
  doc.save(`estimate-${estimate.id.slice(-8)}.pdf`);
};

export const generateInvoicePDF = (invoice: Invoice) => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('IT Management Solutions', 20, 30);
  
  doc.setFontSize(16);
  doc.text('INVOICE', 20, 50);
  
  // Invoice details
  doc.setFontSize(12);
  doc.text(`Invoice #: ${invoice.id.slice(-8)}`, 20, 70);
  doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString()}`, 20, 80);
  
  if (invoice.due_date) {
    doc.text(`Due Date: ${new Date(invoice.due_date).toLocaleDateString()}`, 20, 90);
  }
  
  if (invoice.wave_invoice_id) {
    doc.text(`Wave ID: ${invoice.wave_invoice_id}`, 20, 100);
  }
  
  // Amount
  doc.setFontSize(16);
  doc.text(`Total: $${invoice.total.toFixed(2)}`, 20, 120);
  
  // Status
  doc.setFontSize(12);
  doc.text(`Status: ${invoice.status.toUpperCase()}`, 20, 135);
  
  if (invoice.paid_at) {
    doc.text(`Paid Date: ${new Date(invoice.paid_at).toLocaleDateString()}`, 20, 145);
  }
  
  // Footer
  doc.setFontSize(10);
  doc.text('Thank you for your business!', 20, 250);
  doc.text('IT Management Solutions - Professional IT Services', 20, 260);
  
  // Download
  doc.save(`invoice-${invoice.id.slice(-8)}.pdf`);
};

export const generateReceiptPDF = (receipt: Receipt) => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('IT Management Solutions', 20, 30);
  
  doc.setFontSize(16);
  doc.text('PAYMENT RECEIPT', 20, 50);
  
  // Receipt details
  doc.setFontSize(12);
  doc.text(`Receipt #: ${receipt.id.slice(-8)}`, 20, 70);
  doc.text(`Payment Date: ${new Date(receipt.payment_date).toLocaleDateString()}`, 20, 80);
  doc.text(`Invoice #: ${receipt.invoice_id.slice(-8)}`, 20, 90);
  
  // Amount
  doc.setFontSize(16);
  doc.text(`Amount Paid: $${receipt.payment_amount.toFixed(2)}`, 20, 110);
  
  if (receipt.wave_payment_id) {
    doc.setFontSize(12);
    doc.text(`Payment ID: ${receipt.wave_payment_id}`, 20, 125);
  }
  
  // Footer
  doc.setFontSize(10);
  doc.text('Thank you for your payment!', 20, 250);
  doc.text('IT Management Solutions - Professional IT Services', 20, 260);
  
  // Download
  doc.save(`receipt-${receipt.id.slice(-8)}.pdf`);
};

export const generateContractPDF = (contract: Contract) => {
  const doc = new jsPDF();
  
  // Header
  doc.setFontSize(20);
  doc.text('IT Management Solutions', 20, 30);
  
  doc.setFontSize(16);
  doc.text('SERVICE CONTRACT', 20, 50);
  
  // Contract details
  doc.setFontSize(12);
  doc.text(`Contract #: ${contract.id.slice(-8)}`, 20, 70);
  doc.text(`Estimate #: ${contract.estimate_id.slice(-8)}`, 20, 80);
  doc.text(`Created: ${new Date(contract.created_at).toLocaleDateString()}`, 20, 90);
  
  if (contract.signed_at) {
    doc.text(`Signed: ${new Date(contract.signed_at).toLocaleDateString()}`, 20, 100);
  }
  
  if (contract.signature_data?.signerName) {
    doc.text(`Signed by: ${contract.signature_data.signerName}`, 20, 110);
  }
  
  // Terms of Service
  doc.setFontSize(14);
  doc.text('TERMS OF SERVICE', 20, 130);
  
  doc.setFontSize(10);
  const terms = [
    '1. ACCEPTANCE OF SERVICES',
    'By signing this contract, you agree to the terms and conditions outlined below.',
    '',
    '2. PAYMENT TERMS',
    '- Payment is due within 30 days of invoice date',
    '- Late payments may incur additional fees',
    '- Payment methods accepted: Check, ACH, Credit Card',
    '',
    '3. SERVICE DELIVERY',
    '- Services will be delivered as outlined in the estimate',
    '- Any changes to the scope of work may result in additional charges',
    '- We will provide 30 days of warranty on all services',
    '',
    '4. LIABILITY',
    '- Our liability is limited to the cost of services provided',
    '- Client is responsible for data backup before service begins',
    '- We are not liable for any indirect or consequential damages'
  ];
  
  let yPosition = 145;
  terms.forEach(term => {
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
    doc.text(term, 20, yPosition);
    yPosition += 8;
  });
  
  // Digital signature section if available
  if (contract.signature_data?.signature) {
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(12);
    doc.text('Digital Signature:', 20, yPosition + 10);
    
    // Note: In a real implementation, you'd need to convert the base64 image
    // For now, we'll just add a placeholder
    doc.text('[Digital signature on file]', 20, yPosition + 25);
  }
  
  // Download
  doc.save(`contract-${contract.id.slice(-8)}.pdf`);
};
